import { Box, Button, HStack, Image, Input, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminGetPharmacies } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import ImageModal from "../../../components/Modals/ImageModal";
import { colors } from "../../../styles";
import { IAdminPharmacy, IAdminPharmacyList } from "../../../types";
import { formatDateTimeKor } from "../../../utils";

export default function Pharmacies() {

    /* pagination */

    const [page, setPage] = useState(1);
    const size = 100;

    /* form */

    interface ISearchForm {
        keyword: string;
    }

    const { register, watch, handleSubmit, getValues, formState: {errors, isValid}, setError, setValue } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const [keyword, setKeyword] = useState<string | null>(null);

    const onSubmit = ({ keyword }: ISearchForm) => {
        setPage(1);
        setKeyword(keyword);
    }
    
    /* query */

    const { data: pharmacyListData, isLoading: isPharmacyListDataLoading } = useQuery<IAdminPharmacyList>({
        queryKey: ['adminGetPharmacies', page, size, keyword],
        queryFn: () => adminGetPharmacies({ page, size, keyword }),
        enabled: true,
    });

    const totalPages = useMemo(() => pharmacyListData?.totalPages ?? 0, [pharmacyListData]);
    const currentPage = useMemo(() => pharmacyListData?.currentPage ?? 0, [pharmacyListData]);

    /* pagination */

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* pharmacy columns */

    const pharmacyColumns = [
        { key: "id", label: "ID" },
        { key: "user", label: "약사" },
        { key: "name", label: "이름" },
        { key: "address", label: "주소" },
        { key: "bizRegNo", label: "사업자등록번호" },
        { key: "businessRegistrationCertImage", label: "사업자 등록증" },
        { key: "establishmentRegistrationCertImage", label: "설립 등록증" },
        { key: "healthcareInstitutionId", label: "요양기관번호" },
        { key: "createdAt", label: "생성 일자" },
        { key: "updatedAt", label: "수정 일자" }
    ]

    const { isOpen: isImageModalOpen, onOpen: onOpenImageModal, onClose: onCloseImageModal } = useDisclosure();
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

    const openImage = (url: string) => {
        setSelectedImageUrl(url);
        onOpenImageModal();
    };

    const closeImage = () => {
        setSelectedImageUrl(null);
        onCloseImageModal();
    };

    const renderImageThumbnail = (imageUrl: string | null | undefined, alt: string) => {
        if (!imageUrl) {
            return "-";
        }
        return (
            <Box
                width={8}
                height={8}
                borderRadius={2}
                border={"1px solid " + colors.g5}
                backgroundColor={colors.g5}
                onClick={() => openImage(imageUrl)}
                cursor={"pointer"}
            >
                <Image src={imageUrl} alt={alt} width={"100%"} height={"100%"} objectFit={"cover"} />
            </Box>
        );
    };

    /* render */

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>약국</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{pharmacyListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3}>
                    </HStack>
                </HStack>
                <HStack as="form" onSubmit={handleSubmit(onSubmit)} marginY={10}>
                    <Input 
                        {...register("keyword", {required:false})}
                        type="text" 
                        placeholder="Search" 
                        width={"50%"} 
                    />
                    <Box as="button" type="submit">
                        <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                {/* table */}

                {!isPharmacyListDataLoading ? (
                    <TableContainer marginTop={10}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    {pharmacyColumns.map((column) => (  
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {pharmacyListData?.pharmacies.map((pharmacy) => (
                                    <Tr key={pharmacy.id}>
                                        {pharmacyColumns.map((column, index) => (
                                            <Td key={index}>
                                                {column.key === "user" ? pharmacy.user.name
                                                    : column.key === "businessRegistrationCertImage" ? renderImageThumbnail(pharmacy.businessRegistrationCertImage, "businessRegistrationCertImage")
                                                    : column.key === "establishmentRegistrationCertImage" ? renderImageThumbnail(pharmacy.establishmentRegistrationCertImage, "establishmentRegistrationCertImage")
                                                    : (column.key === "createdAt" || column.key === "updatedAt")
                                                        ? formatDateTimeKor(pharmacy[column.key as keyof IAdminPharmacy] as string)
                                                        : String(pharmacy[column.key as keyof IAdminPharmacy] ?? "-")}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}


                {/* pagination */}

                <HStack justifyContent={"center"} marginTop={5} >
                    {/* 이전 페이지 버튼 */}
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
            
                    {/* 첫 페이지와 '...' */}
                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
            
                    {/* 페이지 버튼들 */}
                    {pages.map((page) => (
                        <Button
                            key={page}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(page)}
                            backgroundColor={page === currentPage ? colors.blue : ""}
                            color={page === currentPage ? colors.white : ""}
                        >
                            {page}
                        </Button>
                    ))}
            
                    {/* 마지막 페이지와 '...' */}
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
            
                    {/* 다음 페이지 버튼 */}
                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>

            {selectedImageUrl &&
                <ImageModal
                    isOpen={isImageModalOpen}
                    onClose={closeImage}
                    imageUrl={selectedImageUrl}
                />
            }
        </AdminOnly>
    )
}